"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { FONT_SERIF } from "../../lib/theme"
import { Button } from "./button"

type DialogSize = "sm" | "md" | "lg" | "xl"

// Each width is gated at the first breakpoint that fits it with 1rem margins;
// below that the base `max-w-[calc(100%-2rem)]` rules, so a wide dialog never
// overflows a narrow viewport.
const SIZE_CLASS: Record<DialogSize, string> = {
  sm: "sm:max-w-[420px]",
  md: "sm:max-w-[560px]",
  lg: "md:max-w-[720px]",
  xl: "lg:max-w-[960px]",
}

// Radix keeps the node mounted until a CSS *animation* on the closed state
// ends, so enter/exit have to be keyframes rather than a transition. No
// animate plugin is installed (the shadcn `animate-in` classes emit no CSS
// here), so the keyframes ship with the component: React hoists this
// `<style precedence>` into <head> once, deduped by href. `scale` and
// `opacity` only — the centring `translate` is a separate property.
const MOTION_CSS = `
@keyframes eqDialogIn { from { opacity: 0; scale: 0.98; } to { opacity: 1; scale: 1; } }
@keyframes eqDialogOut { from { opacity: 1; scale: 1; } to { opacity: 0; scale: 0.98; } }
@keyframes eqDialogVeilIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes eqDialogVeilOut { from { opacity: 1; } to { opacity: 0; } }
[data-slot="dialog-overlay"][data-state="open"] { animation: eqDialogVeilIn 150ms ease-out both; }
[data-slot="dialog-overlay"][data-state="closed"] { animation: eqDialogVeilOut 100ms ease-in both; }
[data-slot="dialog-content"][data-state="open"] { animation: eqDialogIn 150ms ease-out both; }
[data-slot="dialog-content"][data-state="closed"] { animation: eqDialogOut 100ms ease-in both; }
/* A closing dialog takes no clicks: its buttons stay on screen for the exit
   animation, and a double-click would run a primary action twice (a send, a
   supersede). !important beats the layer's inline pointer-events:auto. */
[data-slot="dialog-content"][data-state="closed"] { pointer-events: none !important; }
@media (prefers-reduced-motion: reduce) {
  [data-slot="dialog-overlay"], [data-slot="dialog-content"] { animation: none !important; }
}
`

// A toast is not "outside" the dialog: clicking a toast or its close button
// while a dialog is open must close the toast, not the dialog (and discard what
// was typed in it). Toasts take the pointer through `.eq-toast` in globals.css.
export function keepOpenForToasts(e: { target: EventTarget | null; preventDefault: () => void }) {
  const t = e.target as Element | null
  if (t && typeof t.closest === "function" && t.closest("[data-sonner-toaster]")) e.preventDefault()
}

function DialogMotion() {
  return (
    <style href="eq-dialog-motion" precedence="eq-motion">
      {MOTION_CSS}
    </style>
  )
}

// True inside a `structured` DialogContent: header, body and footer then
// carry their own padding instead of the content's `p-6`.
const StructuredContext = React.createContext(false)

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-[rgba(9,21,37,.55)] backdrop-blur-[4px]",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size = "md",
  structured = false,
  onPointerDownOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  /** Width step: 420 / 560 / 720 / 960 px. Default `md`. */
  size?: DialogSize
  /**
   * Header / body / footer anatomy: the content drops its `p-6 gap-4`,
   * DialogHeader takes the gold rule, DialogBody scrolls, DialogFooter sits
   * on the soft tint. Off by default so existing dialogs keep their padding.
   */
  structured?: boolean
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <StructuredContext.Provider value={structured}>
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-[14px] bg-[#FFFFFF] p-6 shadow-[0_24px_64px_-24px_rgba(9,21,37,.6)] outline-none",
            SIZE_CLASS[size],
            structured && "flex max-h-[calc(100dvh-2rem)] flex-col gap-0 p-0",
            className
          )}
          onPointerDownOutside={(e) => {
            keepOpenForToasts(e)
            onPointerDownOutside?.(e)
          }}
          {...props}
        >
          <DialogMotion />
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className="absolute top-4 right-4 rounded-md p-1 text-[#8899AA] transition-colors duration-150 hover:text-[#1A1A1A] focus-visible:ring-[3px] focus-visible:ring-[rgba(223,192,106,.45)] focus-visible:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </StructuredContext.Provider>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  const structured = React.useContext(StructuredContext)
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex flex-col gap-2 text-center sm:text-left",
        structured &&
          "gap-1 border-b-2 border-[rgba(223,192,106,.35)] px-6 pt-5 pr-12 pb-4 text-left",
        className
      )}
      {...props}
    />
  )
}

function DialogEyebrow({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-eyebrow"
      className={cn("eq-eyebrow eq-eyebrow--gold", className)}
      {...props}
    />
  )
}

function DialogBody({ className, ...props }: React.ComponentProps<"div">) {
  const structured = React.useContext(StructuredContext)
  return (
    <div
      data-slot="dialog-body"
      className={cn(
        structured && "min-h-0 flex-1 overflow-y-auto px-6 py-5",
        className
      )}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  const structured = React.useContext(StructuredContext)
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        structured && "rounded-b-[14px] bg-[#F8F9FB] px-6 py-4",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({
  className,
  style,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-[19px] leading-tight font-bold text-[#1A1A1A]", className)}
      style={{ fontFamily: FONT_SERIF, ...style }}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-[13.5px] leading-snug text-[#5A6B7D]", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogEyebrow,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
